/** Session + capability gating (OAU-05). The rail hides what you can't `can()`,
 *  but the SERVER is the enforcement point — hiding is UX only. */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { auth, type Me } from "@/api/client";

/** Fabric permission gates for PRD-002 — mirrors shared/lib/fabric_auth/permissions.py.
 *  x00 = module gate; function permissions live inside each hundred. */
export const GATE = {
  CONNECT: 100,
  FOTA: 200,
  FLEET: 300,
  ANALYTICS: 400,
  VAULT: 500,
  SECURITY: 600,
  INFRA: 700,
  LINK: 800,
  OPS: 900,
  AI: 1000,
  API: 1100,
  // Added to Fabric's orbit_platform map 2026-09-22. Before this, Drivers,
  // Registry and Asset Catalog all rode CONNECT and Asset Data rode FOTA, so
  // granting one granted four, and "may watch telemetry" could not be
  // separated from "may push firmware".
  DRIVERS: 1200,
  REGISTRY: 1300,
  ASSET_CATALOG: 1400,
  ASSET_DATA: 1500,
  AUDIT: 1600,
  ORGANISATIONS: 1700,
  PRODUCTS: 1800,
} as const;

/** ACTION capabilities — mirrors shared/lib/fabric_auth/plans.py.
 *
 *  Each lives INSIDE its module's x00 gate, so `can()`'s parent rule applies:
 *  closing the module closes all of its actions for free. Hiding a control is UX; `require_cap`
 *  on each route is the enforcement (OAU-05), and the server checks the ROW
 *  besides — a capability says what you may do, never whose asset.
 *
 *  Kept in step with plans.py by
 *  `tests/auth/permissions/test_permission_map_mirror.py`; a drift here is a
 *  button that 403s, or one that is hidden from somebody entitled to it.
 */
export const CAP = {
  // FOTA (inside gate 200)
  PUBLISH_FIRMWARE: 201,
  RUN_BATCHES: 202,
  // Immobilising an asset. Its own id, not GATE_FLEET: opening Fleet is a
  // read, stopping somebody's vehicle is not (OFL-05).
  IMMOBILIZE: 301,
  MANAGE_SIMS: 203,
  FOTA_DEBUG: 204,
  // Asset Catalog (inside gate 1400)
  MANAGE_CATALOG: 1401,
  // Products (inside gate 1800) — SUPER ADMIN ONLY. Held by no plan and no
  // company role, so `can(CAP.MANAGE_PRODUCTS)` is false for every customer
  // and the authoring controls simply are not rendered for them. The server
  // refuses independently; this only avoids showing a button that 403s.
  MANAGE_PRODUCTS: 1801,
  // Organisations (inside gate 1700) — SUPER ADMIN ONLY, same mechanism.
  MANAGE_ORGANISATIONS: 1701,
  // Registry (inside gate 1300)
  TRANSFER_OWNERSHIP: 1301,
  SHIP: 1302,
  ENROL: 1303,
  INSTALL_REMOVE: 1304,
  CHANGE_STATE: 1305,
  ASSIGN_DRIVER: 1306,
  REVOKE_CREDENTIAL: 1307,
} as const;

interface SessionCtx {
  me: Me | null;
  loading: boolean;
  can: (perm: number) => boolean;
  setMe: (me: Me | null) => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // restore on load — a refresh must not log you out (cookie is server-side)
  useEffect(() => {
    auth
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<SessionCtx>(
    () => ({
      me,
      loading,
      setMe,
      can: (perm: number) => {
        if (!me) return false;
        if (!me.granted.includes(perm)) return false;
        const gate = Math.floor(perm / 100) * 100; // parent-gate rule
        return gate === perm || me.granted.includes(gate);
      },
      logout: async () => {
        await auth.logout().catch(() => undefined);
        setMe(null);
      },
    }),
    [me, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}
